import React, { memo, useMemo } from 'react';
import { Tabs, TabPanel } from 'tea-component';
import { useIntl } from 'umi';
import { useTabTemplate } from '../../../Home/hooks/useTabTemplate';
// Tab 图标 - 普通态和激活态
import IntelligentQaIcon from '@/pages/aigc/assets/svg/intelligent-qa-icon.svg';
import IntelligentQaIconActive from '@/pages/aigc/assets/svg/intelligent-qa-icon-active.svg';
import DsReportIcon from '@/pages/aigc/assets/svg/data-science-report-icon.svg';
import DsReportIconActive from '@/pages/aigc/assets/svg/data-science-report-icon-active.svg';
import AiTableIcon from '@/pages/aigc/assets/svg/agent-home-table-icon-default.svg';
import AiTableIconActive from '@/pages/aigc/assets/svg/agent-home-table-icon.svg';
// import MagicPosterIcon from '@/pages/aigc/assets/svg/icon-magical-image-reproduction-default.svg';
// import MagicPosterIconActive from '@/pages/aigc/assets/svg/icon-magical-image-reproduction.svg';
import './index.scss';

// Tab 类型定义
export type CopilotTabType = 'aiAgent' | 'dsReport' | 'aiExcel' | 'magicPoster';

interface ICopilotChatTabsProps {
  /** Tab 切换回调 */
  onTabChangeCallback?: (tabType: CopilotTabType) => void;
  /** AI Agent Tab 内容组件 */
  aiAgentContent: React.ReactNode;
  /** DS Agent Tab 内容组件 */
  dsAgentContent: React.ReactNode;
  /** AI 表格 Tab 内容组件 */
  aiTableContent: React.ReactNode;
  /** 数据魔法海报 Tab 内容组件 */
  magicPosterContent: React.ReactNode;
  /** 是否显示 Tab 内容 */
  showContent?: boolean;
  /** 默认激活的 Tab */
  defaultActiveId?: CopilotTabType;
  /** 本地存储的 key */
  storageKey?: string;
}

// Tab 配置数据
const TAB_CONFIG = [
  {
    id: 'aiAgent' as CopilotTabType,
    labelKey: 'dba_chat_type_ai_agent',
    defaultIcon: IntelligentQaIcon,
    activeIcon: IntelligentQaIconActive,
  },
  {
    id: 'dsReport' as CopilotTabType,
    labelKey: 'dba_chat_type_ds_agent',
    defaultIcon: DsReportIcon,
    activeIcon: DsReportIconActive,
  },
  {
    id: 'aiExcel' as CopilotTabType,
    labelKey: 'dba_chat_type_ai_table',
    defaultIcon: AiTableIcon,
    activeIcon: AiTableIconActive,
  },
  // TODO: 暂不放开数据魔法海报
  // {
  //   id: 'magicPoster' as CopilotTabType,
  //   labelKey: 'dba_chat_type_magic_poster',
  //   defaultIcon: MagicPosterIcon,
  //   activeIcon: MagicPosterIconActive,
  // },
];

const COPILOT_TAB_STORAGE_KEY = '__dba_copilot_tab__';

/**
 * Copilot 聊天 Tab 组件
 * 包含 AI Agent 和 DS Agent 两个 Tab
 */
export const CopilotChatTabs = memo(({
  onTabChangeCallback,
  aiAgentContent,
  dsAgentContent,
  aiTableContent,
  magicPosterContent,
  showContent = true,
  defaultActiveId = 'aiAgent',
  storageKey = COPILOT_TAB_STORAGE_KEY,
}: ICopilotChatTabsProps) => {
  const { formatMessage } = useIntl();

  // 校验 defaultActiveId 是否在 TAB_CONFIG 中，不在则回退到第一个 tab
  const validDefaultActiveId = useMemo(() => {
    const isValid = TAB_CONFIG.some((tab) => tab.id === defaultActiveId);
    return isValid ? defaultActiveId : TAB_CONFIG[0].id;
  }, [defaultActiveId]);

  // 使用 useTabTemplate hooks 管理 Tab 状态
  const {
    activeId,
    onTabChange,
  } = useTabTemplate({
    tabs: TAB_CONFIG,
    defaultActiveId: validDefaultActiveId,
    storageKey,
  });

  // Tab 内容映射
  const contentMap: Record<CopilotTabType, React.ReactNode> = {
    aiAgent: aiAgentContent,
    dsReport: dsAgentContent,
    aiExcel: aiTableContent,
    magicPoster: magicPosterContent,
  };

  // Tab 配置 - 直接使用 TAB_CONFIG 进行 map 循环
  // 注意：始终渲染两个图标（默认态 + 激活态），通过 CSS :hover 和 .is-active 控制显示/隐藏
  // 避免用 JS 状态（hoveredTabId）驱动图标切换，防止 hover 触发 re-render 导致 Tabs 滚动位置被重置
  const tabs = useMemo(() => TAB_CONFIG.map((tab) => {
    return {
      id: tab.id,
      label: (
        <div className="dba-copilot-tab-label">
          <img
            src={tab.defaultIcon}
            alt={`${tab.id}-default`}
            className="dba-copilot-tab-icon dba-copilot-tab-icon--default"
          />
          <img
            src={tab.activeIcon}
            alt={`${tab.id}-active`}
            className="dba-copilot-tab-icon dba-copilot-tab-icon--active"
          />
          <span>{formatMessage({ id: tab.labelKey })}</span>
        </div>
      ),
      // 通过映射获取对应的内容组件
      component: contentMap[tab.id],
    };
  }), [activeId, formatMessage, aiAgentContent, dsAgentContent, aiTableContent, magicPosterContent]);

  const handleTabChangeCallback = (tabType: CopilotTabType) => {
    onTabChange(tabType);
    onTabChangeCallback?.(tabType);
  };

  return (
    <div className="dba-copilot-chat-tabs">
      <Tabs
        tabs={tabs}
        activeId={activeId}
        onActive={(tab) => handleTabChangeCallback(tab.id as CopilotTabType)}
      >
        {/* 首页推荐内容 - 根据 Tab 显示对应 component */}
        {showContent && tabs.map((item) => (
          <TabPanel id={item.id} key={item.id}>
            {item.component}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
});

CopilotChatTabs.displayName = 'CopilotChatTabs';

export default CopilotChatTabs;
